/*
 * QR Code generator library (Kotlin port)
 *
 * Based on the Java implementation by Project Nayuki (MIT License)
 * https://www.nayuki.io/page/qr-code-generator-library
 */

package io.nayuki.qrcodegen

import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min

/**
 * QR Code symbol representation.
 */
class QrCode(
    val version: Int,
    val errorCorrectionLevel: Ecc,
    dataCodewords: ByteArray,
    mask: Int
) {

    val size: Int = version * 4 + 17
    val mask: Int

    private val modules: Array<BooleanArray> = Array(size) { BooleanArray(size) }
    private var isFunction: Array<BooleanArray>? = Array(size) { BooleanArray(size) }

    init {
        require(version in MIN_VERSION..MAX_VERSION) { "Version value out of range" }
        require(mask in -1..7) { "Mask value out of range" }

        drawFunctionPatterns()
        val allCodewords = addEccAndInterleave(dataCodewords)
        drawCodewords(allCodewords)

        var chosenMask = mask
        if (chosenMask == -1) {
            var minPenalty = Int.MAX_VALUE
            for (i in 0 until 8) {
                applyMask(i)
                drawFormatBits(i)
                val penalty = getPenaltyScore()
                if (penalty < minPenalty) {
                    minPenalty = penalty
                    chosenMask = i
                }
                applyMask(i)
            }
        }
        require(chosenMask in 0..7)
        this.mask = chosenMask
        applyMask(chosenMask)
        drawFormatBits(chosenMask)
        isFunction = null
    }

    fun getModule(x: Int, y: Int): Boolean {
        return x in 0 until size && y in 0 until size && modules[y][x]
    }

    private fun drawFunctionPatterns() {
        for (i in 0 until size) {
            setFunctionModule(6, i, i % 2 == 0)
            setFunctionModule(i, 6, i % 2 == 0)
        }

        drawFinderPattern(3, 3)
        drawFinderPattern(size - 4, 3)
        drawFinderPattern(3, size - 4)

        val alignPositions = getAlignmentPatternPositions()
        val numAlign = alignPositions.size
        for (i in 0 until numAlign) {
            for (j in 0 until numAlign) {
                if (!(i == 0 && j == 0 || i == 0 && j == numAlign - 1 || i == numAlign - 1 && j == 0)) {
                    drawAlignmentPattern(alignPositions[i], alignPositions[j])
                }
            }
        }

        drawFormatBits(0)
        drawVersion()
    }

    private fun drawFormatBits(msk: Int) {
        val data = (errorCorrectionLevel.formatBits shl 3) or msk
        var rem = data
        repeat(10) {
            rem = (rem shl 1) xor (((rem ushr 9) * 0x537))
        }
        val bits = (data shl 10 or rem) xor 0x5412
        for (i in 0..5) {
            setFunctionModule(8, i, getBit(bits, i))
        }
        setFunctionModule(8, 7, getBit(bits, 6))
        setFunctionModule(8, 8, getBit(bits, 7))
        setFunctionModule(7, 8, getBit(bits, 8))
        for (i in 9 until 15) {
            setFunctionModule(14 - i, 8, getBit(bits, i))
        }

        for (i in 0 until 8) {
            setFunctionModule(size - 1 - i, 8, getBit(bits, i))
        }
        for (i in 8 until 15) {
            setFunctionModule(8, size - 15 + i, getBit(bits, i))
        }
        setFunctionModule(8, size - 8, true)
    }

    private fun drawVersion() {
        if (version < 7) return
        var rem = version
        repeat(12) {
            rem = (rem shl 1) xor (((rem ushr 11) * 0x1F25))
        }
        val bits = (version shl 12) or rem
        for (i in 0 until 18) {
            val bit = getBit(bits, i)
            val a = size - 11 + i % 3
            val b = i / 3
            setFunctionModule(a, b, bit)
            setFunctionModule(b, a, bit)
        }
    }

    private fun drawFinderPattern(x: Int, y: Int) {
        for (dy in -4..4) {
            for (dx in -4..4) {
                val dist = max(abs(dx), abs(dy))
                val xx = x + dx
                val yy = y + dy
                if (xx in 0 until size && yy in 0 until size) {
                    setFunctionModule(xx, yy, dist != 2 && dist != 4)
                }
            }
        }
    }

    private fun drawAlignmentPattern(x: Int, y: Int) {
        for (dy in -2..2) {
            for (dx in -2..2) {
                setFunctionModule(x + dx, y + dy, max(abs(dx), abs(dy)) != 1)
            }
        }
    }

    private fun setFunctionModule(x: Int, y: Int, isDark: Boolean) {
        modules[y][x] = isDark
        isFunction?.get(y)?.let { it[x] = true }
    }

    private fun addEccAndInterleave(data: ByteArray): ByteArray {
        require(data.size == getNumDataCodewords(version, errorCorrectionLevel))
        val numBlocks = NUM_ERROR_CORRECTION_BLOCKS[errorCorrectionLevel.ordinal][version].toInt()
        val blockEccLen = ECC_CODEWORDS_PER_BLOCK[errorCorrectionLevel.ordinal][version].toInt()
        val rawCodewords = getNumRawDataModules(version) / 8
        val numShortBlocks = numBlocks - rawCodewords % numBlocks
        val shortBlockLen = rawCodewords / numBlocks

        val blocks = Array(numBlocks) { ByteArray(0) }
        val rsDiv = reedSolomonComputeDivisor(blockEccLen)
        var k = 0
        for (i in 0 until numBlocks) {
            val dataLen = shortBlockLen - blockEccLen + if (i < numShortBlocks) 0 else 1
            val dat = data.copyOfRange(k, k + dataLen)
            k += dataLen
            val block = ByteArray(shortBlockLen + 1)
            dat.copyInto(block, 0)
            val ecc = reedSolomonComputeRemainder(dat, rsDiv)
            ecc.copyInto(block, block.size - blockEccLen)
            blocks[i] = block
        }

        val result = ByteArray(rawCodewords)
        var index = 0
        for (i in 0 until blocks[0].size) {
            for (j in blocks.indices) {
                if (i != shortBlockLen - blockEccLen || j >= numShortBlocks) {
                    result[index] = blocks[j][i]
                    index++
                }
            }
        }
        return result
    }

    private fun drawCodewords(data: ByteArray) {
        require(data.size == getNumRawDataModules(version) / 8)
        var bitIndex = 0
        var right = size - 1
        while (right >= 1) {
            if (right == 6) right = 5
            for (vert in 0 until size) {
                for (j in 0 until 2) {
                    val x = right - j
                    val upward = ((right + 1) and 2) == 0
                    val y = if (upward) size - 1 - vert else vert
                    val func = isFunction?.get(y)?.get(x) ?: false
                    if (!func && bitIndex < data.size * 8) {
                        val bit = getBit(data[bitIndex ushr 3].toInt(), 7 - (bitIndex and 7))
                        modules[y][x] = bit
                        bitIndex++
                    }
                }
            }
            right -= 2
        }
        require(bitIndex == data.size * 8)
    }

    private fun applyMask(msk: Int) {
        require(msk in 0..7) { "Mask value out of range" }
        for (y in 0 until size) {
            for (x in 0 until size) {
                val func = isFunction?.get(y)?.get(x) ?: false
                if (func) continue
                val invert = when (msk) {
                    0 -> (x + y) % 2 == 0
                    1 -> y % 2 == 0
                    2 -> x % 3 == 0
                    3 -> (x + y) % 3 == 0
                    4 -> (x / 3 + y / 2) % 2 == 0
                    5 -> (x * y) % 2 + (x * y) % 3 == 0
                    6 -> ((x * y) % 2 + (x * y) % 3) % 2 == 0
                    7 -> ((x + y) % 2 + (x * y) % 3) % 2 == 0
                    else -> false
                }
                if (invert) {
                    modules[y][x] = !modules[y][x]
                }
            }
        }
    }

    private fun getPenaltyScore(): Int {
        var result = 0

        for (y in 0 until size) {
            var runColor = false
            var runLength = 0
            val runHistory = IntArray(7)
            for (x in 0 until size) {
                if (modules[y][x] == runColor) {
                    runLength++
                    if (runLength == 5) result += PENALTY_N1
                    else if (runLength > 5) result++
                } else {
                    finderPenaltyAddHistory(runLength, runHistory)
                    if (!runColor) result += finderPenaltyCountPatterns(runHistory) * PENALTY_N3
                    runColor = modules[y][x]
                    runLength = 1
                }
            }
            result += finderPenaltyTerminateAndCount(runColor, runLength, runHistory) * PENALTY_N3
        }

        for (x in 0 until size) {
            var runColor = false
            var runLength = 0
            val runHistory = IntArray(7)
            for (y in 0 until size) {
                if (modules[y][x] == runColor) {
                    runLength++
                    if (runLength == 5) result += PENALTY_N1
                    else if (runLength > 5) result++
                } else {
                    finderPenaltyAddHistory(runLength, runHistory)
                    if (!runColor) result += finderPenaltyCountPatterns(runHistory) * PENALTY_N3
                    runColor = modules[y][x]
                    runLength = 1
                }
            }
            result += finderPenaltyTerminateAndCount(runColor, runLength, runHistory) * PENALTY_N3
        }

        for (y in 0 until size - 1) {
            for (x in 0 until size - 1) {
                val color = modules[y][x]
                if (color == modules[y][x + 1] && color == modules[y + 1][x] && color == modules[y + 1][x + 1]) {
                    result += PENALTY_N2
                }
            }
        }

        var dark = 0
        modules.forEach { row ->
            row.forEach { if (it) dark++ }
        }
        val total = size * size
        val k = (abs(dark * 20 - total * 10) + total - 1) / total - 1
        result += k * PENALTY_N4
        return result
    }

    private fun getAlignmentPatternPositions(): IntArray {
        if (version == 1) return IntArray(0)
        val numAlign = version / 7 + 2
        val step = ((version * 8 + numAlign * 3 + 5) / (numAlign * 4 - 4)) * 2
        val result = IntArray(numAlign)
        result[0] = 6
        var pos = size - 7
        for (i in result.size - 1 downTo 1) {
            result[i] = pos
            pos -= step
        }
        return result
    }

    private fun finderPenaltyCountPatterns(runHistory: IntArray): Int {
        val n = runHistory[1]
        val core = n > 0 && runHistory[2] == n && runHistory[3] == n * 3 &&
            runHistory[4] == n && runHistory[5] == n
        var count = 0
        if (core && runHistory[0] >= n * 4 && runHistory[6] >= n) count++
        if (core && runHistory[6] >= n * 4 && runHistory[0] >= n) count++
        return count
    }

    private fun finderPenaltyTerminateAndCount(currentRunColor: Boolean, currentRunLength: Int, runHistory: IntArray): Int {
        var color = currentRunColor
        var length = currentRunLength
        if (color) {
            finderPenaltyAddHistory(length, runHistory)
            length = 0
        }
        length += size
        finderPenaltyAddHistory(length, runHistory)
        return finderPenaltyCountPatterns(runHistory)
    }

    private fun finderPenaltyAddHistory(currentRunLength: Int, runHistory: IntArray) {
        var length = currentRunLength
        if (runHistory[0] == 0) {
            length += size
        }
        for (i in runHistory.size - 1 downTo 1) {
            runHistory[i] = runHistory[i - 1]
        }
        runHistory[0] = length
    }

    companion object {
        const val MIN_VERSION = 1
        const val MAX_VERSION = 40

        private const val PENALTY_N1 = 3
        private const val PENALTY_N2 = 3
        private const val PENALTY_N3 = 40
        private const val PENALTY_N4 = 10

        private val ECC_CODEWORDS_PER_BLOCK = arrayOf(
            byteArrayOf(-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30),
            byteArrayOf(-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28),
            byteArrayOf(-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30),
            byteArrayOf(-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30)
        )

        private val NUM_ERROR_CORRECTION_BLOCKS = arrayOf(
            byteArrayOf(-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25),
            byteArrayOf(-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49),
            byteArrayOf(-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68),
            byteArrayOf(-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81)
        )

        fun encodeText(text: CharSequence, ecl: Ecc): QrCode {
            val segments = QrSegment.makeSegments(text)
            return encodeSegments(segments, ecl)
        }

        fun encodeBinary(data: ByteArray, ecl: Ecc): QrCode {
            val segment = QrSegment.makeBytes(data)
            return encodeSegments(listOf(segment), ecl)
        }

        fun encodeSegments(segs: List<QrSegment>, ecl: Ecc): QrCode {
            return encodeSegments(segs, ecl, MIN_VERSION, MAX_VERSION, -1, true)
        }

        fun encodeSegments(
            segs: List<QrSegment>,
            ecl: Ecc,
            minVersion: Int,
            maxVersion: Int,
            mask: Int,
            boostEcl: Boolean
        ): QrCode {
            require(minVersion in MIN_VERSION..MAX_VERSION && maxVersion in MIN_VERSION..MAX_VERSION && minVersion <= maxVersion)
            require(mask in -1..7)

            var version = minVersion
            var dataUsedBits: Int
            while (true) {
                val dataCapacityBits = getNumDataCodewords(version, ecl) * 8
                dataUsedBits = QrSegment.getTotalBits(segs, version)
                if (dataUsedBits != -1 && dataUsedBits <= dataCapacityBits) break
                if (version >= maxVersion) {
                    val msg = if (dataUsedBits != -1) {
                        "Data length = $dataUsedBits bits, Max capacity = $dataCapacityBits bits"
                    } else {
                        "Segment too long"
                    }
                    throw DataTooLongException(msg)
                }
                version++
            }

            var errorLevel = ecl
            if (boostEcl) {
                for (newEcl in Ecc.values()) {
                    if (dataUsedBits <= getNumDataCodewords(version, newEcl) * 8) {
                        errorLevel = newEcl
                    }
                }
            }

            val bb = BitBuffer()
            for (seg in segs) {
                bb.appendBits(seg.mode.modeBits, 4)
                bb.appendBits(seg.numChars, seg.mode.numCharCountBits(version))
                bb.appendData(seg.getData())
            }

            val dataCapacityBits = getNumDataCodewords(version, errorLevel) * 8
            bb.appendBits(0, min(4, dataCapacityBits - bb.bitLength))
            bb.appendBits(0, (8 - bb.bitLength % 8) % 8)
            require(bb.bitLength % 8 == 0)

            var padByte = 0xEC
            while (bb.bitLength < dataCapacityBits) {
                bb.appendBits(padByte, 8)
                padByte = padByte xor 0xEC xor 0x11
            }

            val dataCodewords = ByteArray(bb.bitLength / 8)
            for (i in 0 until bb.bitLength) {
                val value = dataCodewords[i ushr 3].toInt() or (bb.getBit(i) shl (7 - (i and 7)))
                dataCodewords[i ushr 3] = value.toByte()
            }

            return QrCode(version, errorLevel, dataCodewords, mask)
        }

        private fun getNumRawDataModules(ver: Int): Int {
            require(ver in MIN_VERSION..MAX_VERSION) { "Version number out of range" }
            val size = ver * 4 + 17
            var result = size * size
            result -= 8 * 8 * 3
            result -= 15 * 2 + 1
            result -= (size - 16) * 2
            if (ver >= 2) {
                val numAlign = ver / 7 + 2
                result -= (numAlign - 1) * (numAlign - 1) * 25
                result -= (numAlign - 2) * 2 * 20
                if (ver >= 7) {
                    result -= 6 * 3 * 2
                }
            }
            return result
        }

        private fun reedSolomonComputeDivisor(degree: Int): ByteArray {
            require(degree in 1..255) { "Degree out of range" }
            val result = ByteArray(degree)
            result[degree - 1] = 1
            var root = 1
            for (i in 0 until degree) {
                for (j in 0 until result.size) {
                    result[j] = reedSolomonMultiply(result[j].toInt() and 0xFF, root).toByte()
                    if (j + 1 < result.size) {
                        val value = result[j].toInt() xor result[j + 1].toInt()
                        result[j] = value.toByte()
                    }
                }
                root = reedSolomonMultiply(root, 0x02)
            }
            return result
        }

        private fun reedSolomonComputeRemainder(data: ByteArray, divisor: ByteArray): ByteArray {
            val result = ByteArray(divisor.size)
            for (b in data) {
                val factor = (b.toInt() xor result[0].toInt()) and 0xFF
                for (i in 0 until result.size - 1) {
                    result[i] = result[i + 1]
                }
                result[result.size - 1] = 0
                for (i in result.indices) {
                    val mult = reedSolomonMultiply(divisor[i].toInt() and 0xFF, factor)
                    result[i] = (result[i].toInt() xor mult).toByte()
                }
            }
            return result
        }

        private fun reedSolomonMultiply(x: Int, y: Int): Int {
            var xx = x
            var yy = y
            var z = 0
            for (i in 7 downTo 0) {
                z = (z shl 1) xor ((z ushr 7) * 0x11D)
                z = z xor (((yy ushr i) and 1) * xx)
            }
            return z and 0xFF
        }

        internal fun getNumDataCodewords(ver: Int, ecl: Ecc): Int {
            return getNumRawDataModules(ver) / 8 -
                ECC_CODEWORDS_PER_BLOCK[ecl.ordinal][ver] * NUM_ERROR_CORRECTION_BLOCKS[ecl.ordinal][ver]
        }

        fun getBit(x: Int, i: Int): Boolean = ((x ushr i) and 1) != 0
    }

    enum class Ecc(val formatBits: Int) {
        LOW(1),
        MEDIUM(0),
        QUARTILE(3),
        HIGH(2)
    }
}
